import { useEffect, useRef, useState } from 'react'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../../lib/api.jsx'

const TAG_LABELS = {
  'interested': 'Interested', 'not-interested': 'Not interested',
  'meeting-booked': 'Meeting', 'auto-reply': 'Auto reply',
  'ooo': 'OOO', 'unsubscribe': 'Unsub',
  'question': 'Question', 'follow-up': 'Follow up',
}

// Simple module-level cache: 60s TTL keyed by summary qs.
const _cache = new Map()
const CACHE_TTL_MS = 60_000

export default function CampaignDrawer({
  open,
  onClose,
  selectedCampaignIds,
  onChange,
  qs,
  onNamesLoaded,
}) {
  const [data, setData] = useState({
    data: [],
    uncategorized: { total: 0, unread: 0 },
    hasMore: false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [retryTick, setRetryTick] = useState(0)
  const [expandedRow, setExpandedRow] = useState(null)
  const drawerRef = useRef(null)
  const closeBtnRef = useRef(null)

  // Build summary qs: same as list qs but strip campaign_ids (we want all).
  const summaryQs = (() => {
    const p = new URLSearchParams(qs || '')
    p.delete('campaign_ids')
    return p.toString()
  })()

  useEffect(() => {
    if (!open) return
    const cached = _cache.get(summaryQs)
    if (cached && Date.now() - cached.t < CACHE_TTL_MS) {
      setData(cached.v)
      onNamesLoaded?.(cached.v.data || [])
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get(`/inbox/campaigns/summary${summaryQs ? `?${summaryQs}` : ''}`)
      .then((res) => {
        if (cancelled) return
        const v = res.data || { data: [], uncategorized: { total: 0, unread: 0 }, hasMore: false }
        _cache.set(summaryQs, { v, t: Date.now() })
        setData(v)
        onNamesLoaded?.(v.data || [])
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, summaryQs, retryTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Focus management + Escape close
  useEffect(() => {
    if (!open) return
    const prev = document.activeElement
    closeBtnRef.current?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      prev?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const toggleRow = (id) =>
    setExpandedRow((prev) => (prev === id ? null : id))

  const toggleSelected = (id) => {
    const next = selectedCampaignIds.includes(id)
      ? selectedCampaignIds.filter((x) => x !== id)
      : [...selectedCampaignIds, id]
    onChange(next)
  }

  const clearAll = () => onChange([])

  const retry = () => {
    _cache.delete(summaryQs)
    setRetryTick((n) => n + 1)
  }

  return (
    <>
      <div className="rh-drawer-backdrop" onClick={onClose} />
      <aside
        className="rh-drawer rh-drawer--open"
        role="dialog"
        aria-label="Campaigns"
        ref={drawerRef}
      >
        <div className="rh-drawer-header">
          <strong>Campaigns</strong>
          <button
            ref={closeBtnRef}
            type="button"
            className="rh-drawer-close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <X size={14} />
          </button>
        </div>

        <button
          type="button"
          className={`rh-drawer-row rh-drawer-row--all ${
            selectedCampaignIds.length === 0 ? 'rh-drawer-row--selected' : ''
          }`}
          onClick={clearAll}
        >
          <span>All campaigns</span>
        </button>

        {loading && <div className="rh-drawer-loading">Loading…</div>}
        {error && (
          <div className="rh-drawer-error">
            Failed to load.
            <button type="button" onClick={retry}>
              Retry
            </button>
          </div>
        )}

        <div className="rh-drawer-list">
          {!loading && !error && data.data.length === 0 && (
            <div className="rh-drawer-empty">No campaigns yet.</div>
          )}
          {data.data.map((c) => {
            const isSelected = selectedCampaignIds.includes(c.campaignId)
            const isExpanded = expandedRow === c.campaignId
            return (
              <div
                key={c.campaignId}
                className={`rh-drawer-row ${isSelected ? 'rh-drawer-row--selected' : ''}`}
              >
                <label className="rh-drawer-row__main">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelected(c.campaignId)}
                    aria-label={`Select campaign ${c.campaignName}`}
                  />
                  <span className="rh-drawer-row__name">{c.campaignName}</span>
                  <span className="rh-drawer-row__counts">
                    <span className="rh-drawer-row__total">{c.total}</span>
                    {c.unread > 0 && (
                      <span className="rh-drawer-row__unread">{c.unread}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="rh-drawer-row__chev"
                    onClick={(e) => {
                      e.preventDefault()
                      toggleRow(c.campaignId)
                    }}
                    aria-label={isExpanded ? 'Hide tag breakdown' : 'Show tag breakdown'}
                  >
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </label>
                {isExpanded && (
                  <div className="rh-drawer-row__tags">
                    {Object.entries(c.tagCounts || {}).map(([tag, n]) => (
                      <span key={tag} className="rh-drawer-tag-chip">
                        {TAG_LABELS[tag] || tag} · {n}
                      </span>
                    ))}
                    {Object.keys(c.tagCounts || {}).length === 0 && (
                      <span className="rh-drawer-tag-chip rh-drawer-tag-chip--empty">
                        No tags yet
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="rh-drawer-uncat">
          Uncategorized · {data.uncategorized.total} total
          {data.uncategorized.unread > 0 && ` · ${data.uncategorized.unread} unread`}
        </div>
      </aside>
    </>
  )
}
